import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import DateTimePicker from 'react-widgets/lib/DateTimePicker';

/* Same as the `DateTimePicker` but uses a Moment object instead of Date.
 * Includes support for timezones. More information: https://git.io/v1unK */
class MomentDateTimePicker extends Component {
  static conversionFormat = 'dddd, MMMM D, YYYY, h:mm A' // Valid moment format without timezone

  static propTypes = {
    format: PropTypes.string,
    onChange: PropTypes.func,
    useTimezone: PropTypes.string,
    minimized: PropTypes.bool,
    onlyIcon: PropTypes.bool,
    alignRight: PropTypes.bool,
    innerRef: PropTypes.func,
    value(props, name) {
      if (props[name] && !moment.isMoment(props[name])) {
        return new Error(`${name} must be a moment.js date instance`);
      }
    },
    parser(props, name) {
      if (props[name]) return new Error('Custom parsers are not supported');
    },
  }

  static defaultProps = {
    onChange() {}, // Default to no-op
    useTimezone: moment.tz.guess(),
    minimized: false,
    onlyIcon: false,
    alignRight: false,
    innerRef() {}
  }

  handleChange = (value, str) => {
    const date = value ? moment.tz(moment(value).format(MomentDateTimePicker.conversionFormat),
    MomentDateTimePicker.conversionFormat, this.props.useTimezone) : null;
    this.props.onChange(date, str);
  }

  render() {
    const formatter = (date) => (date ? moment(date).format(this.props.format) : '');
    const parser = (dateStr) => {
      if (dateStr && moment(dateStr, this.props.format).isValid()) {
        return moment(dateStr, this.props.format).toDate();
      }
      return null;
    };
    let value = undefined;

    if (this.props.value) {
      value = this.props.value.tz(this.props.useTimezone);
      value = new Date(value.format(MomentDateTimePicker.conversionFormat));
    }

    const props = Object.assign({}, this.props);
    delete props.useTimezone;
    return (
      <DateTimePicker
        {...props}
        value={value}
        ref={this.props.innerRef}
        onChange={this.handleChange}
        className={`${this.props.minimized ? 'minimized u-flex' : ''} ${this.props.onlyIcon ? 'only-icon u-flex' : ''} ${this.props.alignRight ? 'align-right' : ''}`}
        format={formatter}
        parse={parser}
      />
    );
  }
}

/* Date and time picker that separates date from time into two inputs for usability.
 * Dropdown opens when you click anywhere on the input.
 * Adds an hidden field to send the value to the server.
 * Contains support for custom events. */
export default class DateTimeInput extends Component {
  static propTypes = {
    date: PropTypes.bool,
    defaultValue: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    reversed: PropTypes.bool,
    time: PropTypes.bool,
    triggerEvents: PropTypes.bool,
    hiddenFieldFormat: PropTypes.string,
    dateFieldFormat: PropTypes.string,
    min: PropTypes.string,
    max: PropTypes.string,
    autoCompleteIncrement: PropTypes.number,
    useTimezone: PropTypes.string,
    minimized: PropTypes.bool,
    onlyIcon: PropTypes.bool,
    alignRight: PropTypes.bool,
    innerRef: PropTypes.func,
    format(props, name) {
      if (props[name]) return new Error('Custom formats are not supported');
    },
  }

  static defaultProps = {
    defaultValue: '',
    reversed: false,
    date: true,
    onChange() {},
    time: true,
    triggerEvents: false,
    hiddenFieldFormat: '',
    dateFieldFormat: 'MMMM DD, YYYY',
    min: null,
    max: null,
    autoCompleteIncrement: null,
    useTimezone: moment.tz.guess(),
    minimized: false,
    onlyIcon: false,
    alignRight: false,
    innerRef() {}
  }

  constructor(props) {
    super(props);

    let date;
    let time;

    if (this.props.defaultValue) {
      time = moment.tz(this.props.defaultValue, this.props.useTimezone);
      date = time;
    }

    this.state = { date, time, open: false };
  }

  componentDidMount() {
    if (this.props.autoCompleteIncrement !== null) {
      $(document).on('datetimeinput:change', this.handleAutoCompleteEvent);
    }
  }

  componentWillUnmount() {
    $(document).off('datetimeinput:change', this.handleAutoCompleteEvent);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.defaultValue) {
      const date = moment.tz(nextProps.defaultValue, nextProps.useTimezone);
      const time = date;
      this.setState({ date, time });
    }
  }

  // Returns a datetime object based on the date and time savd on state
  getDatetimeFromState() {
    let datetime;

    // If this is both a date and time input, require both values to be set
    if (this.props.date && this.props.time && this.state.date && this.state.time) {
      datetime = this.state.date.clone();
      datetime.hours(this.state.time.hours());
      datetime.minutes(this.state.time.minutes());
      datetime.seconds(0);
    // If this is a date input and there is a date
    } else if (this.props.date && !this.props.time && this.state.date) {
      datetime = this.state.date.clone();
    // If this is a time input and there is a time
    } else if (!this.props.date && this.props.time && this.state.time) {
      datetime = this.state.time.clone();
      datetime.seconds(0);
    }
    return datetime;
  }

  afterChange() {
    const datetime = this.getDatetimeFromState();
    this.props.onChange(datetime ? new Date(datetime.format()) : null);
    this.triggerCustomEvent(datetime ? datetime.format() : null);
  }

  handleDateChange = (date) => {
    this.setState({ date }, this.afterChange);
  }

  handleTimeChange = (time) => {
    this.setState({ time }, this.afterChange);
  }

  handleAutoCompleteEvent = (_, startTime, triggerName) => {
    if (startTime !== null) {
      $(document).off('datetimeinput:change', this.handleAutoCompleteEvent);

      // Remove the listener and exit if the triggered input was this input
      if (triggerName === this.props.name) { return; }

      const endTime = moment.tz(startTime, this.props.useTimezone).add({ minutes: this.props.autoCompleteIncrement });
      this.handleDateChange(endTime);
      this.handleTimeChange(endTime);
    }
  }

  // In the views you can listen to the custom event. You should use a parent
  // element if you want to bind before the react component is mounted.
  triggerCustomEvent(value) {
    if (this.props.triggerEvents) {
      const $node = $(ReactDOM.findDOMNode(this));
      $node.find('input[type=text]').trigger('datetimeinput:change', [value, this.props.name]);
    }
  }

  // In order to open the picker on click, we handle it with handleClick() and
  // handleToggle(). More info: https://github.com/jquense/react-widgets/issues/257
  handleDateClick = (event) => {
    if (event.target.tagName === 'INPUT') {
      this.setState({ open: 'date' });
    }
  }

  handleTimeClick = (event) => {
    if (event.target.tagName === 'INPUT') {
      this.setState({ open: 'time' });
    }
  }

  handleToggle = (open) => {
    this.setState({ open });
  }

  render() {
    const datetime = this.getDatetimeFromState();
    const pickers = [];

    const props = Object.assign({}, this.props);
    delete props.name;
    delete props.triggerEvents;
    delete props.hiddenFieldFormat;
    delete props.autoCompleteIncrement;
    if (props.min) { props.min = new Date(this.props.min); } else { delete props.min; }
    if (props.max) { props.max = new Date(this.props.max); } else { delete props.max; }

    // Setup the date picker
    if (props.date) {
      let className = this.props.triggerEvents ? 'validatableDateWidget ' : '';
      className += props.time ? 'u-w-2/3' : 'u-w-full';

      pickers.push(
        <div key="calendar" className={className}>
          <MomentDateTimePicker {...props}
            defaultValue={this.state.date}
            value={this.state.date}
            onChange={this.handleDateChange}
            onClick={this.handleDateClick}
            onToggle={this.handleToggle}
            minimized={this.props.minimized}
            onlyIcon={this.props.onlyIcon}
            alignRight={this.props.alignRight}
            innerRef={this.props.innerRef}
            date
            time={false}
            format={this.props.dateFieldFormat}
            open={this.state.open === 'date' ? 'date' : false}
          />
        </div>
      );
    }
    // Setup the time picker
    if (props.time) {
      let className = this.props.triggerEvents ? 'validatableTimeWidget ' : '';
      className += props.date ? 'u-w-1/3' : 'u-w-full';
      // this jiggling is because of an issue with firefox not rendering the
      // dropdown in the same way as chrome, and it has some other implications
      // when interacting with the component. This is a hack, but a necessary
      // one to minimize buggy interactions
      const min = ((datetime && datetime.clone()) || moment()).tz(moment.tz.guess()).subtract(1, 'year').startOf('day').toDate();
      const max = ((datetime && datetime.clone()) || moment()).tz(moment.tz.guess()).add(1, 'year').endOf('day').toDate();
      // DateTimePicker's TimePicker has a bug that makes it so the chosen time
      // doesn't show up as selected in the dropdown list for any day other than
      // the current day. The code below clones and changes the chosen time so
      // that it is based on the current day. This change doesn't affect the
      // hidden field's value so it has no effect on form submission, etc.
      // Relevant code: https://github.com/jquense/react-widgets/blob/master/packages/react-widgets/src/TimeList.js
      const currentDate = moment(new Date());
      let time = null;
      if (this.state.time) {
        time = moment(this.state.time.clone())
          .tz(this.props.useTimezone)
          .set({ year: currentDate.year(), month: currentDate.month(), date: currentDate.date() });
      }

      const timePicker = (
        <div key="time" className={className}>
          <MomentDateTimePicker {...props}
            defaultValue={time}
            value={time}
            min={min}
            max={max}
            onChange={this.handleTimeChange}
            onClick={this.handleTimeClick}
            onToggle={this.handleToggle}
            minimized={this.props.minimized}
            onlyIcon={this.props.onlyIcon}
            alignRight={this.props.alignRight}
            innerRef={this.props.innerRef}
            date={false}
            time
            format="h:mm A"
            open={this.state.open === 'time' ? 'time' : false}
          />
        </div>
      );

      if (props.reversed) {
        pickers.unshift(timePicker);
      } else {
        pickers.push(timePicker);
      }
    }
    return (
      <div className="u-flex">
        { pickers }
        <input
          type="hidden"
          name={this.props.name}
          value={datetime ? datetime.format(this.props.hiddenFieldFormat) : ''}
        />
      </div>
    );
  }
}
